import { toInputDate } from "@/utils/dateUtils";
import { useState, useEffect } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { useDealerPortal } from "@/hooks/useDealerPortal";
import { dealerPortalLabels } from "@/i18n";
import svVehicleCard from "@/i18n/locales/sv/vehicleCard.json";
import enVehicleCard from "@/i18n/locales/en/vehicleCard.json";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { restoreNativeSheetAfterDismissVeto } from "@/lib/native/restoreNativeSheetAfterDismissVeto";
import { useDealerPortalNativeSheetProps } from "@/lib/native/useDealerPortalNativeSheetProps";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { FileDown, Loader2, CalendarIcon } from "lucide-react";
import type { DealerPortalVehicle } from "@/types/dealerPortal";
import type { VehiclePdfServiceType } from "./vehiclePdfCards";
import { getVehiclePdfActionCards } from "./vehiclePdfCards";
import { PdfGenerationDialog } from "@/components/ui/PdfGenerationDialog";
import {
  generateGlassPDF,
  generateFullmaktPDF,
  generateInsurancePDF,
  generateValuationPDF,
  generateServiceSchemaPDF,
  generateVDNPDF,
  serviceSchemaItemKeys,
  getServiceSchemaItemLabels,
  type GlassFormData,
  type FullmaktFormData,
  type InsuranceFormData,
  type ValuationFormData,
  type ServiceSchemaFormData,
  type VDNFormData,
} from "@/utils/dealerPortalPdfGenerators";
import DamageSketchCanvas from "@/components/dealer-portal/DamageSketchCanvas";
import type { jsPDF } from "jspdf";

async function saveGeneratedPdf(doc: jsPDF, fileName: string): Promise<void> {
  const { savePdfDocument } = await import("@/lib/native/savePdf");
  await savePdfDocument(doc, fileName);
}

/** Shared section wrapper – card style with rounded corners, border, subtle grey background. */
function PdfFormSection({
  heading,
  description,
  children,
}: {
  heading: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border bg-muted/40 dark:bg-muted/30 px-3 py-2.5 space-y-2">
      <div>
        <h3 className="text-xs font-semibold text-foreground tracking-tight">
          {heading}
        </h3>
        {description && (
          <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
            {description}
          </p>
        )}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

/** Read-only vehicle info box shown at top of all PDF dialogs. For valuation, pass valuationForm to show Färg and Skick inside the box. */
function VehicleInfoBox({
  vehicle,
  valuationForm = null,
}: {
  vehicle: DealerPortalVehicle;
  valuationForm?: ValuationFormData | null;
}) {
  const { language } = useLanguage();
  const vcLocale = language === "en" ? enVehicleCard : svVehicleCard;
  const vc = vcLocale as {
    color: Record<string, string>;
    condition: Record<string, string>;
  };
  const pdf = dealerPortalLabels(language).pdfForms as Record<string, unknown>;
  const vbox = (pdf?.vehicleInfoBox ?? {}) as Record<string, string>;
  const translate = (
    category: "color" | "condition",
    value: string | undefined,
  ) => {
    if (!value?.trim()) return "–";
    const key = value.toLowerCase().replace(/\s+/g, "_");
    return vc[category][key] ?? value;
  };

  const regNr = vehicle.regNr ?? "–";
  const makeModelYear =
    [vehicle.make, vehicle.model, vehicle.year].filter(Boolean).join(" ") ||
    "–";
  const milUnit = vbox.milUnit ?? "mil";
  const mileage =
    vehicle.mileage != null ? `${vehicle.mileage} ${milUnit}` : "–";
  const vin = vehicle.vin ?? "–";

  return (
    <div className="rounded-lg border border-border bg-muted/40 dark:bg-muted/30 px-3 py-2 text-xs">
      <p className="text-muted-foreground text-[11px] mb-2 leading-snug">
        {vbox.intro ??
          "Fordonsuppgifterna hämtas från fordonskortet. Uppdatera under Fordonsdetaljer om något inte stämmer."}
      </p>
      <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
        <span className="text-muted-foreground">{vbox.regNr ?? "Reg.nr:"}</span>
        <span>{regNr}</span>
        <span className="text-muted-foreground">
          {vbox.vehicle ?? "Fordon:"}
        </span>
        <span>{makeModelYear}</span>
        <span className="text-muted-foreground">
          {vbox.mileage ?? "Mätarställning:"}
        </span>
        <span>{mileage}</span>
        <span className="text-muted-foreground">
          {vbox.vin ?? "VIN/Chassinr:"}
        </span>
        <span>{vin}</span>
        {valuationForm && (
          <>
            <span className="text-muted-foreground">
              {vbox.color ?? "Färg:"}
            </span>
            <span>{translate("color", valuationForm.color)}</span>
            <span className="text-muted-foreground">
              {vbox.condition ?? "Skick:"}
            </span>
            <span>{translate("condition", valuationForm.condition)}</span>
          </>
        )}
      </div>
    </div>
  );
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  serviceType: VehiclePdfServiceType | null;
  vehicle: DealerPortalVehicle | null;
}

const defaultGlassForm = (v: DealerPortalVehicle): GlassFormData => ({
  insuranceCompany: "",
  ownerName: "",
  ownerPersonOrg: "",
  phoneDay: "",
  vatRegistered: null,
  registration: v.regNr ?? "",
  carModel: `${v.make ?? ""} ${v.model ?? ""}`.trim(),
  damageYear: "",
  damageMonth: "",
  damageDay: "",
  damageLocation: "",
  damageReasonStenskott: false,
  damageReasonInbrott: false,
  damageReasonOther: "",
  damageMomentGlas: false,
  damageMomentStold: false,
  damageMomentOther: "",
  signatureDate: toInputDate(new Date()),
});

const defaultValuationForm = (v: DealerPortalVehicle): ValuationFormData => ({
  registration: v.regNr ?? "",
  vehicle: `${v.make ?? ""} ${v.model ?? ""} ${v.year ?? ""}`.trim(),
  vin: v.vin ?? "",
  mileage: v.mileage != null ? String(v.mileage) : "",
  color: v.color ?? "",
  purpose: "Forsaljning",
  purposeOther: "",
  condition: v.condition ?? "",
  extraEquipment: v.equipment?.length ? v.equipment.join(", ") : "",
  estimatedPurchasePrice: "",
  remarks: [],
  validityPeriod: "30",
  notes: "",
});

const defaultFullmaktForm = (v: DealerPortalVehicle): FullmaktFormData => ({
  registration: v.regNr ?? "",
  vehicle: `${v.make ?? ""} ${v.model ?? ""} ${v.year ?? ""}`.trim(),
  vin: v.vin ?? "",
  giverName: "",
  giverPersonOrg: "",
  giverAddress: "",
  giverPostalCity: "",
  giverPhone: "",
  giverEmail: "",
  recipientName: "",
  recipientPersonOrg: "",
  recipientAddress: "",
  recipientPostalCity: "",
  recipientPhone: "",
  fullmaktType: "",
  customActions: "",
  validFrom: toInputDate(new Date()),
  validTo: "",
  isUnlimited: false,
  signatureDate: "",
  signaturePlace: "",
});

const defaultInsuranceForm = (v: DealerPortalVehicle): InsuranceFormData => ({
  registration: v.regNr ?? "",
  vehicle: `${v.make ?? ""} ${v.model ?? ""} ${v.year ?? ""}`.trim(),
  vin: v.vin ?? "",
  mileage: v.mileage != null ? String(v.mileage) : "",
  insuranceCompany: "",
  insuranceCompanyOther: "",
  policyNumber: "",
  damageDate: "",
  damageTime: "",
  damageLocation: "",
  damageLocationType: "road",
  damageType: "",
  damageDescription: "",
  carSketchType: "car",
  carSketchData: "",
  sceneSketchType: "road",
  sceneSketchData: "",
  hasCounterpart: false,
  counterpartName: "",
  counterpartInsurance: "",
  counterpartRegistration: "",
  counterpartPhone: "",
  hasWitnesses: false,
  witnessInfo: "",
  hasPoliceReport: false,
  policeReportNumber: "",
  damagedParts: "",
  estimatedCost: "",
  contactName: "",
  contactPhone: "",
  contactEmail: "",
  signatureDate: "",
  signaturePlace: "",
});

const defaultServiceSchemaForm = (
  v: DealerPortalVehicle,
): ServiceSchemaFormData => ({
  customerName: "",
  customerAddress: "",
  customerPostal: "",
  serviceType: "Basservice",
  serviceInterval: "2000mil/1år",
  extraService: "Nej",
  registration: v.regNr ?? "",
  make: v.make ?? "",
  model: v.model ?? "",
  year: String(v.year ?? ""),
  date: toInputDate(new Date()),
  mileage: "",
  checkedItems: {},
  comments: {},
  signatureDate: "",
});

const defaultVDNForm = (v: DealerPortalVehicle): VDNFormData => ({
  sellerName: "",
  sellerOrgNr: "",
  sellerAddress: "",
  sellerPostalCity: "",
  sellerPhone: "",
  sellerEmail: "",
  buyerName: "",
  buyerPersonNr: "",
  buyerAddress: "",
  buyerPostalCity: "",
  buyerPhone: "",
  buyerEmail: "",
  registration: v.regNr ?? "",
  make: v.make ?? "",
  model: v.model ?? "",
  variant: v.variant ?? "",
  modelYear: String(v.year ?? ""),
  firstRegistrationDate: "",
  vin: v.vin ?? "",
  mileage: v.mileage != null ? String(v.mileage) : "",
  mileageGuaranteed: true,
  color: v.color ?? "",
  fuel: v.fuelType ?? "",
  transmission: v.transmission ?? "",
  engineVolume: v.engineSize ?? "",
  enginePower: v.horsepower != null ? String(v.horsepower) : "",
  environmentClass: "",
  co2Emission: "",
  numberOfOwners: "",
  importVehicle: false,
  importCountry: "",
  taxPaid: true,
  taxDebtAmount: "",
  numberOfKeys: "2",
  registrationCertificate: true,
  lastInspectionDate: "",
  inspectionResult: "",
  inspectionRemarks: "",
  nextInspectionDate: "",
  serviceBookExists: true,
  serviceBookDigital: false,
  lastServiceDate: "",
  lastServiceMileage: "",
  lastServiceType: "",
  timingBeltChanged: false,
  timingBeltMileage: "",
  timingBeltDate: "",
  testDrivePerformed: false,
  testDriveDate: "",
  testDriveMileage: "",
  hasAccidentHistory: false,
  accidentDescription: "",
  hasRepaintedParts: false,
  repaintedPartsDescription: "",
  hasRustDamage: false,
  rustDescription: "",
  summerTiresIncluded: true,
  summerTiresBrand: "",
  summerTiresDimension: "",
  summerTiresDepth: "",
  winterTiresIncluded: false,
  winterTiresBrand: "",
  winterTiresDimension: "",
  winterTiresDepth: "",
  spareTireIncluded: false,
  isElectricOrHybrid: false,
  batteryCapacity: "",
  batteryHealth: "",
  chargingCableIncluded: false,
  estimatedRange: "",
  equipmentList: v.equipment?.length ? v.equipment.join(", ") : "",
  airCondition: false,
  navigation: false,
  towBar: false,
  sunroof: false,
  heatedSeats: false,
  parkingSensors: false,
  reverseCamera: false,
  cruiseControl: false,
  leatherInterior: false,
  engineCondition: "Normalt",
  transmissionCondition: "Normalt",
  brakeCondition: "Normalt",
  suspensionCondition: "Normalt",
  bodyCondition: "Normalt",
  interiorCondition: "Normalt",
  electricalCondition: "Normalt",
  exhaustCondition: "Normalt",
  steeringCondition: "Normalt",
  lightsCondition: "Normalt",
  remarks: "",
  knownDefects: "",
  guaranteeType: "",
  guaranteePeriod: "",
  guaranteeMileage: "",
  guaranteeConditions: "",
  reclamationInfo: "",
  price: "",
  vatIncluded: true,
  depositAmount: "",
  paymentMethod: "",
  financingArranged: false,
  deliveryDate: "",
  deliveryLocation: "",
  isDistanceSale: false,
  withdrawalRightInfo: "",
  signatureDate: "",
  signaturePlace: "",
});

export function DealerPortalServicePdfDialog({
  open,
  onOpenChange,
  serviceType,
  vehicle,
}: Props) {
  const { language } = useLanguage();
  const { company } = useDealerPortal();
  const pf = dealerPortalLabels(language).pdfForms as Record<
    string,
    string | Record<string, string>
  >;
  const cards = getVehiclePdfActionCards(language);

  const [isGenerating, setIsGenerating] = useState(false);
  const [glassForm, setGlassForm] = useState<GlassFormData | null>(null);
  const [valuationForm, setValuationForm] = useState<ValuationFormData | null>(
    null,
  );
  const [fullmaktForm, setFullmaktForm] = useState<FullmaktFormData | null>(
    null,
  );
  const [insuranceForm, setInsuranceForm] = useState<InsuranceFormData | null>(
    null,
  );
  const [serviceSchemaForm, setServiceSchemaForm] =
    useState<ServiceSchemaFormData | null>(null);
  const [vdnForm, setVdnForm] = useState<VDNFormData | null>(null);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const nativeSheetProps = useDealerPortalNativeSheetProps(open);

  useEffect(() => {
    if (!showConfirmClose && open) {
      restoreNativeSheetAfterDismissVeto();
    }
  }, [showConfirmClose, open]);

  useEffect(() => {
    if (!open || !vehicle) return;
    setGlassForm(defaultGlassForm(vehicle));
    setValuationForm(defaultValuationForm(vehicle));
    setFullmaktForm(defaultFullmaktForm(vehicle));
    setInsuranceForm(defaultInsuranceForm(vehicle));
    setServiceSchemaForm(defaultServiceSchemaForm(vehicle));
    setVdnForm(defaultVDNForm(vehicle));
  }, [open, vehicle?.id]);

  const card = serviceType ? cards.find((c) => c.id === serviceType) : null;
  const title = card?.title ?? "PDF";

  const toastSuccess = (pf?.toastSuccessGlass ??
    pf?.toastSuccess ??
    "PDF skapad och nedladdad") as string;
  const toastSuccessMap: Record<string, string> = {
    glass: (pf?.toastSuccessGlass ?? toastSuccess) as string,
    fullmakt: (pf?.toastSuccessFullmakt ?? toastSuccess) as string,
    insurance: (pf?.toastSuccessInsurance ?? toastSuccess) as string,
    valuation: (pf?.toastSuccessValuation ?? toastSuccess) as string,
    serviceschema: (pf?.toastSuccessServiceschema ?? toastSuccess) as string,
    vdn: (pf?.toastSuccessVdn ?? toastSuccess) as string,
  };
  const toastErr = (pf?.toastError ?? "Kunde inte skapa PDF") as string;

  const generatingTitle = (pf?.generatingTitle as string) ?? "Genererar PDF";
  const generatingFor =
    (pf?.generatingFor as string) ?? "Genererar PDF för {name}";
  const generatingFallback =
    (pf?.generatingFallback as string) ?? "Genererar PDF...";
  const fileLabel = (pf?.fileLabel as string) ?? "Fil:";
  const pleaseWait =
    (pf?.pleaseWait as string) ?? "Vänligen vänta medan PDF:en genereras...";

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen === false) {
      setShowConfirmClose(true);
      return;
    }
    onOpenChange(nextOpen);
  };

  const handleConfirmClose = () => {
    setShowConfirmClose(false);
    onOpenChange(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!serviceType || !vehicle) return;
    const dateStr = toInputDate(new Date());
    let fileName = "";
    try {
      setIsGenerating(true);
      await new Promise((r) => setTimeout(r, 0));
      if (serviceType === "glass" && glassForm) {
        fileName = `skadeanmalan_${glassForm.registration}_${dateStr}.pdf`;
        const doc = generateGlassPDF(glassForm, language);
        await saveGeneratedPdf(doc, fileName);
        toast.success(toastSuccessMap.glass);
      } else if (serviceType === "fullmakt" && fullmaktForm) {
        fileName = `fullmakt_${fullmaktForm.registration}_${dateStr}.pdf`;
        const doc = generateFullmaktPDF(
          fullmaktForm,
          language,
          company?.data?.name ?? undefined,
        );
        await saveGeneratedPdf(doc, fileName);
        toast.success(toastSuccessMap.fullmakt);
      } else if (serviceType === "insurance" && insuranceForm) {
        fileName = `forsakringsanmalan_${insuranceForm.registration}_${dateStr}.pdf`;
        const doc = generateInsurancePDF(
          insuranceForm,
          language,
          company?.data?.name ?? undefined,
        );
        await saveGeneratedPdf(doc, fileName);
        toast.success(toastSuccessMap.insurance);
      } else if (serviceType === "valuation" && valuationForm) {
        fileName = `varderingsintyg_${valuationForm.registration}_${dateStr}.pdf`;
        const doc = generateValuationPDF(
          valuationForm,
          company?.data?.name,
          language,
        );
        await saveGeneratedPdf(doc, fileName);
        toast.success(toastSuccessMap.valuation);
      } else if (serviceType === "serviceschema" && serviceSchemaForm) {
        fileName = `serviceschema_${serviceSchemaForm.registration}_${dateStr}.pdf`;
        const doc = generateServiceSchemaPDF(serviceSchemaForm, language);
        await saveGeneratedPdf(doc, fileName);
        toast.success(toastSuccessMap.serviceschema);
      } else if (serviceType === "vdn" && vdnForm) {
        fileName = `varudeklaration_${vdnForm.registration}_${dateStr}.pdf`;
        const doc = generateVDNPDF(vdnForm, language);
        await saveGeneratedPdf(doc, fileName);
        toast.success(toastSuccessMap.vdn);
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(toastErr);
    } finally {
      setIsGenerating(false);
    }
  };

  if (!serviceType || !vehicle) return null;

  const generatingMessage = card
    ? generatingFor.replace("{name}", card.title)
    : generatingFallback;

  const getExpectedFileName = (): string | undefined => {
    if (!card) return undefined;
    const dateStr = toInputDate(new Date());
    const reg = vehicle.regNr ?? "fordon";
    const base =
      {
        glass: "skadeanmalan",
        fullmakt: "fullmakt",
        insurance: "forsakringsanmalan",
        valuation: "varderingsintyg",
        serviceschema: "serviceschema",
        vdn: "varudeklaration",
      }[serviceType ?? ""] ?? "pdf";
    return `${base}_${reg}_${dateStr}.pdf`;
  };

  return (
    <>
      <PdfGenerationDialog
        isOpen={isGenerating}
        testName={card?.title}
        fileName={getExpectedFileName()}
        title={generatingTitle}
        message={generatingMessage}
        fileLabel={fileLabel}
        pleaseWait={pleaseWait}
      />
      <AlertDialog
        open={showConfirmClose}
        onOpenChange={(next) => {
          setShowConfirmClose(next);
          if (!next && open) {
            restoreNativeSheetAfterDismissVeto();
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {(pf?.dialog as Record<string, string>)?.closeWithoutSaveTitle ??
                "Stäng utan att spara?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {(pf?.dialog as Record<string, string>)
                ?.closeWithoutSaveDescription ??
                "Om du stänger nu kommer all information du fyllt i att försvinna. Är du säker på att du vill stänga?"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {(pf?.dialog as Record<string, string>)?.continueEditing ??
                "Fortsätt redigera"}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmClose}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {(pf?.dialog as Record<string, string>)?.close ?? "Stäng"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Dialog
        open={open}
        onOpenChange={handleOpenChange}
        beforeDismiss={() => {
          setShowConfirmClose(true);
          return false;
        }}
      >
        <DialogContent
          {...nativeSheetProps}
          className={cn(
            nativeSheetProps.className,
            serviceType === "glass"
              ? "max-w-xl max-h-[calc(100dvh-2rem)] sm:max-h-[90vh] flex flex-col p-0"
              : "max-w-2xl max-h-[calc(100dvh-2rem)] sm:max-h-[90vh] flex flex-col p-0",
          )}
        >
          <DialogHeader className="px-4 sm:px-6 pt-10 sm:pt-6 pb-2 border-b shrink-0">
            <DialogTitle className="text-sm font-semibold">{title}</DialogTitle>
            <p className="text-xs text-muted-foreground mt-0.5">
              {card?.description}
            </p>
          </DialogHeader>
          <form
            onSubmit={handleSubmit}
            className="flex flex-col min-h-0 flex-1 overflow-hidden"
          >
            <div className="px-4 sm:px-6 py-3 overflow-y-auto flex-1 space-y-3 min-h-0 [&_input]:h-8 [&_input]:text-xs [&_input]:bg-muted/50 [&_input]:border-border [&_textarea]:text-xs [&_textarea]:bg-muted/50 [&_textarea]:border-border [&_button[role=combobox]]:h-8 [&_button[role=combobox]]:text-xs [&_button[role=combobox]]:bg-muted/50 [&_button[role=combobox]]:border-border [&_label]:text-xs [&_label]:font-medium">
              <VehicleInfoBox
                vehicle={vehicle}
                valuationForm={
                  serviceType === "valuation" ? valuationForm : undefined
                }
              />
              {serviceType === "glass" && glassForm && (
                <GlassFormFields
                  form={glassForm}
                  setForm={setGlassForm}
                  labels={(pf?.glass as Record<string, string>) ?? {}}
                />
              )}
              {serviceType === "fullmakt" && fullmaktForm && (
                <FullmaktFormFields
                  form={fullmaktForm}
                  setForm={setFullmaktForm}
                  labels={(pf?.fullmakt as Record<string, string>) ?? {}}
                  fullmaktTypes={
                    (pf?.fullmaktTypes as Record<string, string>) ?? {}
                  }
                />
              )}
              {serviceType === "insurance" && insuranceForm && (
                <InsuranceFormFields
                  form={insuranceForm}
                  setForm={setInsuranceForm}
                  labels={(pf?.insurance as Record<string, string>) ?? {}}
                  damageTypes={
                    (pf?.insuranceDamageTypes as Record<string, string>) ?? {}
                  }
                />
              )}
              {serviceType === "valuation" && valuationForm && (
                <ValuationFormFields
                  form={valuationForm}
                  setForm={setValuationForm}
                  labels={(pf?.valuation as Record<string, string>) ?? {}}
                />
              )}
              {serviceType === "serviceschema" && serviceSchemaForm && (
                <ServiceSchemaFormFields
                  form={serviceSchemaForm}
                  setForm={setServiceSchemaForm}
                  labels={(pf?.serviceschema as Record<string, string>) ?? {}}
                  types={
                    (pf?.serviceschemaTypes as Record<string, string>) ?? {}
                  }
                  intervals={
                    (pf?.serviceschemaIntervals as Record<string, string>) ?? {}
                  }
                />
              )}
              {serviceType === "vdn" && vdnForm && (
                <VDNFormFields
                  form={vdnForm}
                  setForm={setVdnForm}
                  labels={(pf?.vdn as Record<string, string>) ?? {}}
                  vehicle={vehicle}
                />
              )}
            </div>
            <div className="px-4 sm:px-6 py-4 border-t shrink-0 flex flex-col sm:flex-row justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isGenerating}
              >
                {(pf?.cancel as string) ?? "Avbryt"}
              </Button>
              <Button type="submit" className="gap-2" disabled={isGenerating}>
                {isGenerating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FileDown className="h-4 w-4" />
                )}
                {(pf?.createPdf as string) ?? "Skapa PDF"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

// --- Form field components (one per PDF type) ---

function GlassFormFields({
  form,
  setForm,
  labels = {},
}: {
  form: GlassFormData;
  setForm: React.Dispatch<React.SetStateAction<GlassFormData | null>>;
  labels?: Record<string, string>;
}) {
  const l = labels;

  return (
    <div className="space-y-4">
      {/* Försäkrings- och bilägaruppgifter */}
      <PdfFormSection
        heading={l.sectionContact ?? "Försäkrings- och bilägaruppgifter"}
        description={
          l.sectionContactDesc ??
          "Uppgifter om försäkringsbolag och bilägare för skadeanmälan."
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>{l.insuranceCompany ?? "Försäkringsbolag"}</Label>
            <Input
              value={form.insuranceCompany}
              onChange={(e) =>
                setForm({ ...form, insuranceCompany: e.target.value })
              }
            />
          </div>
          <div className="space-y-2">
            <Label>{l.ownerName ?? "Bilägarens namn"}</Label>
            <Input
              value={form.ownerName}
              onChange={(e) => setForm({ ...form, ownerName: e.target.value })}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>{l.ownerPersonOrg ?? "Person/Org.nr"}</Label>
            <Input
              value={form.ownerPersonOrg}
              onChange={(e) =>
                setForm({ ...form, ownerPersonOrg: e.target.value })
              }
              placeholder={l.placeholderPersonOrg ?? "ÅÅÅÅMMDD-XXXX"}
            />
          </div>
          <div className="space-y-2">
            <Label>{l.phoneDay ?? "Telefon dagtid"}</Label>
            <Input
              value={form.phoneDay}
              onChange={(e) => setForm({ ...form, phoneDay: e.target.value })}
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Label className="text-sm font-medium shrink-0">
            {l.vatRegistered ?? "Moms redovisningsskyldig"}
          </Label>
          <div className="flex gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={form.vatRegistered === true}
                onCheckedChange={() =>
                  setForm({ ...form, vatRegistered: true })
                }
              />
              <span className="text-sm">{l.yes ?? "Ja"}</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={form.vatRegistered === false}
                onCheckedChange={() =>
                  setForm({ ...form, vatRegistered: false })
                }
              />
              <span className="text-sm">{l.no ?? "Nej"}</span>
            </label>
          </div>
        </div>
      </PdfFormSection>

      {/* Skadeinformation */}
      <PdfFormSection
        heading={l.sectionDamage ?? "Skadeinformation"}
        description={
          l.sectionDamageDesc ??
          "När och var skadan inträffade samt orsak och skademoment."
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>{l.damageDate ?? "Skadedatum"}</Label>
            <div className="grid grid-cols-3 gap-2">
              <Input
                placeholder={l.damageDatePlaceholderYear ?? "ÅÅÅÅ"}
                maxLength={4}
                value={form.damageYear}
                onChange={(e) =>
                  setForm({ ...form, damageYear: e.target.value })
                }
                className="text-center"
              />
              <Input
                placeholder={l.damageDatePlaceholderMonth ?? "MM"}
                maxLength={2}
                value={form.damageMonth}
                onChange={(e) =>
                  setForm({ ...form, damageMonth: e.target.value })
                }
                className="text-center"
              />
              <Input
                placeholder={l.damageDatePlaceholderDay ?? "DD"}
                maxLength={2}
                value={form.damageDay}
                onChange={(e) =>
                  setForm({ ...form, damageDay: e.target.value })
                }
                className="text-center"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>{l.damagePlace ?? "Skadeplats"}</Label>
            <Input
              value={form.damageLocation}
              onChange={(e) =>
                setForm({ ...form, damageLocation: e.target.value })
              }
            />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>{l.damageReason ?? "Skadeorsak"}</Label>
            <div className="flex flex-wrap items-center gap-3 gap-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={form.damageReasonStenskott}
                  onCheckedChange={(c) =>
                    setForm({ ...form, damageReasonStenskott: !!c })
                  }
                />
                <span className="text-sm">
                  {l.damageReasonStenskott ?? "Stenskott"}
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={form.damageReasonInbrott}
                  onCheckedChange={(c) =>
                    setForm({ ...form, damageReasonInbrott: !!c })
                  }
                />
                <span className="text-sm">
                  {l.damageReasonInbrott ?? "Inbrott"}
                </span>
              </label>
            </div>
            <Input
              className="mt-1"
              placeholder={l.damageReasonOther ?? "Annat"}
              value={form.damageReasonOther}
              onChange={(e) =>
                setForm({ ...form, damageReasonOther: e.target.value })
              }
            />
          </div>
          <div className="space-y-2">
            <Label>{l.damageMoment ?? "Skademoment"}</Label>
            <div className="flex flex-wrap items-center gap-3 gap-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={form.damageMomentGlas}
                  onCheckedChange={(c) =>
                    setForm({ ...form, damageMomentGlas: !!c })
                  }
                />
                <span className="text-sm">{l.damageMomentGlas ?? "Glas"}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={form.damageMomentStold}
                  onCheckedChange={(c) =>
                    setForm({ ...form, damageMomentStold: !!c })
                  }
                />
                <span className="text-sm">
                  {l.damageMomentStold ?? "Stöld"}
                </span>
              </label>
            </div>
            <Input
              className="mt-1"
              placeholder={l.damageMomentOther ?? "Annat"}
              value={form.damageMomentOther}
              onChange={(e) =>
                setForm({ ...form, damageMomentOther: e.target.value })
              }
            />
          </div>
        </div>
      </PdfFormSection>

      {/* Intygstext */}
      <p className="text-xs text-muted-foreground">
        {l.certificationText ??
          "Härmed intygas riktigheten av ovanstående uppgifter samt att försäkringen var gällande vid skadetillfället och omfattar valt skademoment."}
      </p>

      {/* Underskrift */}
      <PdfFormSection
        heading={l.sectionSignature ?? "Underskrift"}
        description={
          l.sectionSignatureDesc ?? "Datum och signatur för anmälan."
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
          <div className="space-y-2 min-w-0">
            <Label className="text-muted-foreground">{l.date ?? "Datum"}</Label>
            <div className="relative">
              <Input
                type="date"
                value={form.signatureDate}
                onChange={(e) =>
                  setForm({ ...form, signatureDate: e.target.value })
                }
                className="pr-9 [&::-webkit-calendar-picker-indicator]:opacity-0"
              />
              <CalendarIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground pointer-events-none" />
            </div>
          </div>
          <div className="space-y-2 min-w-0">
            <Label className="text-muted-foreground block">
              {l.signatureLabel ??
                "Försäkringstagarens eller förarens underskrift"}
            </Label>
            <Input
              readOnly
              placeholder={l.signaturePlaceholder ?? "Digital signatur"}
              className="bg-muted/50"
            />
          </div>
        </div>
      </PdfFormSection>
    </div>
  );
}

function FullmaktFormFields({
  form,
  setForm,
  labels = {},
  fullmaktTypes: typeLabels = {},
}: {
  form: FullmaktFormData;
  setForm: React.Dispatch<React.SetStateAction<FullmaktFormData | null>>;
  labels?: Record<string, string>;
  fullmaktTypes?: Record<string, string>;
}) {
  const l = labels;
  const fullmaktTypes = [
    { value: "agarbyte", label: typeLabels.agarbyte ?? "Ägarbyte/Försäljning" },
    {
      value: "avregistrering",
      label: typeLabels.avregistrering ?? "Avregistrering",
    },
    { value: "export", label: typeLabels.export ?? "Export" },
    { value: "skyltning", label: typeLabels.skyltning ?? "Skyltning" },
    { value: "service", label: typeLabels.service ?? "Service" },
    { value: "forsakring", label: typeLabels.forsakring ?? "Försäkring" },
    { value: "besiktning", label: typeLabels.besiktning ?? "Besiktning" },
    { value: "allting", label: typeLabels.allting ?? "Samtliga åtgärder" },
  ];

  return (
    <div className="space-y-4">
      <PdfFormSection
        heading={l.sectionGiver ?? "Fullmaktsgivare"}
        description={l.sectionGiverDesc}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>{l.giverName ?? "Fullmaktsgivare – Namn"}</Label>
            <Input
              value={form.giverName}
              onChange={(e) => setForm({ ...form, giverName: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label>{l.giverPersonOrg ?? "Personnr/Org.nr"}</Label>
            <Input
              value={form.giverPersonOrg}
              onChange={(e) =>
                setForm({ ...form, giverPersonOrg: e.target.value })
              }
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label>{l.giverAddress ?? "Adress"}</Label>
          <Input
            value={form.giverAddress}
            onChange={(e) => setForm({ ...form, giverAddress: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label>{l.giverPostalCity ?? "Postnummer, Ort"}</Label>
          <Input
            value={form.giverPostalCity}
            onChange={(e) =>
              setForm({ ...form, giverPostalCity: e.target.value })
            }
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>{l.giverPhone ?? "Telefon"}</Label>
            <Input
              value={form.giverPhone}
              onChange={(e) => setForm({ ...form, giverPhone: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label>{l.giverEmail ?? "E-post"}</Label>
            <Input
              type="email"
              value={form.giverEmail}
              onChange={(e) => setForm({ ...form, giverEmail: e.target.value })}
            />
          </div>
        </div>
      </PdfFormSection>
      <PdfFormSection
        heading={l.sectionRecipient ?? "Fullmaktstagare"}
        description={l.sectionRecipientDesc}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>{l.recipientName ?? "Fullmaktstagare – Namn/Företag"}</Label>
            <Input
              value={form.recipientName}
              onChange={(e) =>
                setForm({ ...form, recipientName: e.target.value })
              }
            />
          </div>
          <div className="space-y-2">
            <Label>{l.recipientPersonOrg ?? "Org.nr/Personnr"}</Label>
            <Input
              value={form.recipientPersonOrg}
              onChange={(e) =>
                setForm({ ...form, recipientPersonOrg: e.target.value })
              }
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label>{l.recipientAddress ?? "Adress"}</Label>
          <Input
            value={form.recipientAddress}
            onChange={(e) =>
              setForm({ ...form, recipientAddress: e.target.value })
            }
          />
        </div>
        <div className="space-y-2">
          <Label>{l.recipientPostalCity ?? "Postnummer, Ort"}</Label>
          <Input
            value={form.recipientPostalCity}
            onChange={(e) =>
              setForm({ ...form, recipientPostalCity: e.target.value })
            }
          />
        </div>
        <div className="space-y-2">
          <Label>{l.recipientPhone ?? "Telefon"}</Label>
          <Input
            value={form.recipientPhone}
            onChange={(e) =>
              setForm({ ...form, recipientPhone: e.target.value })
            }
          />
        </div>
      </PdfFormSection>
      <PdfFormSection
        heading={l.sectionScope ?? "Fullmaktens omfattning"}
        description={l.sectionScopeDesc}
      >
        <div className="space-y-2">
          <Label>{l.fullmaktType ?? "Fullmakts typ"}</Label>
          <Select
            value={form.fullmaktType}
            onValueChange={(v) => setForm({ ...form, fullmaktType: v })}
          >
            <SelectTrigger>
              <SelectValue placeholder={l.selectType ?? "Välj typ"} />
            </SelectTrigger>
            <SelectContent>
              {fullmaktTypes.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>
            {l.customActionsLabel ?? "Särskilda befogenheter (valfritt)"}
          </Label>
          <Textarea
            value={form.customActions}
            onChange={(e) =>
              setForm({ ...form, customActions: e.target.value })
            }
            placeholder={
              l.customActionsPlaceholder ??
              "Ange eventuella särskilda åtgärder som fullmakten omfattar..."
            }
            rows={3}
          />
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            checked={form.isUnlimited}
            onCheckedChange={(c) => setForm({ ...form, isUnlimited: !!c })}
          />
          <Label>{l.isUnlimited ?? "Gäller tillsvidare"}</Label>
        </div>
        {!form.isUnlimited && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>{l.validFrom ?? "Giltig från"}</Label>
              <Input
                type="date"
                value={form.validFrom}
                onChange={(e) =>
                  setForm({ ...form, validFrom: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>{l.validTo ?? "Giltig till"}</Label>
              <Input
                type="date"
                value={form.validTo}
                onChange={(e) => setForm({ ...form, validTo: e.target.value })}
              />
            </div>
          </div>
        )}
      </PdfFormSection>
      <PdfFormSection
        heading={l.sectionSignature ?? "Underskrift"}
        description={l.sectionSignatureDesc}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>{l.signaturePlace ?? "Ort (underskrift)"}</Label>
            <Input
              value={form.signaturePlace}
              onChange={(e) =>
                setForm({ ...form, signaturePlace: e.target.value })
              }
            />
          </div>
          <div className="space-y-2">
            <Label>{l.date ?? "Datum"}</Label>
            <Input
              type="date"
              value={form.signatureDate}
              onChange={(e) =>
                setForm({ ...form, signatureDate: e.target.value })
              }
            />
          </div>
        </div>
        {l.certificationText && (
          <p className="text-xs text-muted-foreground pt-2">
            {l.certificationText}
          </p>
        )}
      </PdfFormSection>
    </div>
  );
}

function InsuranceFormFields({
  form,
  setForm,
  labels = {},
  damageTypes = {},
}: {
  form: InsuranceFormData;
  setForm: React.Dispatch<React.SetStateAction<InsuranceFormData | null>>;
  labels?: Record<string, string>;
  damageTypes?: Record<string, string>;
}) {
  const l = labels;
  const dt = damageTypes;

  return (
    <div className="space-y-4">
      <PdfFormSection
        heading={l.sectionClaim ?? "Försäkrings- och skadeuppgifter"}
        description={l.sectionClaimDesc}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>{l.insuranceCompany ?? "Försäkringsbolag"}</Label>
            <Select
              value={form.insuranceCompany || ""}
              onValueChange={(v) => setForm({ ...form, insuranceCompany: v })}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={l.selectInsurance ?? "Välj försäkringsbolag"}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="If">If</SelectItem>
                <SelectItem value="Folksam">Folksam</SelectItem>
                <SelectItem value="Trygg-Hansa">Trygg-Hansa</SelectItem>
                <SelectItem value="Länsförsäkringar">
                  Länsförsäkringar
                </SelectItem>
                <SelectItem value="Moderna Försäkringar">
                  Moderna Försäkringar
                </SelectItem>
                <SelectItem value="Gjensidige">Gjensidige</SelectItem>
                <SelectItem value="Dina Försäkringar">
                  Dina Försäkringar
                </SelectItem>
                <SelectItem value="annat">
                  {l.insuranceOther ?? "Annat"}
                </SelectItem>
              </SelectContent>
            </Select>
            {form.insuranceCompany === "annat" && (
              <Input
                value={form.insuranceCompanyOther}
                onChange={(e) =>
                  setForm({ ...form, insuranceCompanyOther: e.target.value })
                }
                placeholder={
                  l.insuranceCompanyOtherPlaceholder ?? "Ange försäkringsbolag"
                }
                className="mt-2"
              />
            )}
          </div>
          <div className="space-y-2">
            <Label>{l.policyNumber ?? "Försäkringsnummer"}</Label>
            <Input
              value={form.policyNumber}
              onChange={(e) =>
                setForm({ ...form, policyNumber: e.target.value })
              }
            />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{l.damageDate ?? "Skadedatum"}</Label>
            <Input
              type="date"
              value={form.damageDate}
              onChange={(e) => setForm({ ...form, damageDate: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label>{l.damageTime ?? "Skadetid"}</Label>
            <div className="flex gap-2">
              <Select
                value={
                  form.damageTime
                    ? (form.damageTime.split(":")[0] ?? "00")
                    : "00"
                }
                onValueChange={(hour) => {
                  const min = form.damageTime?.split(":")[1] ?? "00";
                  setForm({
                    ...form,
                    damageTime: `${hour.padStart(2, "0")}:${min.padStart(2, "0")}`,
                  });
                }}
              >
                <SelectTrigger className="w-[72px]">
                  <SelectValue placeholder="Tim" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 24 }, (_, i) => (
                    <SelectItem key={i} value={String(i).padStart(2, "0")}>
                      {String(i).padStart(2, "0")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={
                  form.damageTime
                    ? (form.damageTime.split(":")[1] ?? "00")
                    : "00"
                }
                onValueChange={(minute) => {
                  const hour = form.damageTime?.split(":")[0] ?? "00";
                  setForm({
                    ...form,
                    damageTime: `${hour.padStart(2, "0")}:${minute.padStart(2, "0")}`,
                  });
                }}
              >
                <SelectTrigger className="w-[72px]">
                  <SelectValue placeholder="Min" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 60 }, (_, i) => (
                    <SelectItem key={i} value={String(i).padStart(2, "0")}>
                      {String(i).padStart(2, "0")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </PdfFormSection>
      <PdfFormSection
        heading={l.sectionDamage ?? "Skadebeskrivning"}
        description={l.sectionDamageDesc}
      >
        <div className="space-y-2">
          <Label>{l.damagePlace ?? "Skadeplats"}</Label>
          <Input
            value={form.damageLocation}
            onChange={(e) =>
              setForm({ ...form, damageLocation: e.target.value })
            }
            placeholder={l.damagePlacePlaceholder}
          />
        </div>
        <div className="space-y-2">
          <Label>{l.damageType ?? "Typ av skada"}</Label>
          <Select
            value={form.damageType}
            onValueChange={(v) => setForm({ ...form, damageType: v })}
          >
            <SelectTrigger>
              <SelectValue
                placeholder={
                  l.selectDamageType ?? l.selectType ?? "Välj skadetyp"
                }
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="trafikolycka">
                {dt.trafikolycka ?? "Trafikolycka"}
              </SelectItem>
              <SelectItem value="parkering">
                {dt.parkering ?? "Parkeringsskada"}
              </SelectItem>
              <SelectItem value="vandalism">
                {dt.vandalism ?? "Vandalism"}
              </SelectItem>
              <SelectItem value="stold">{dt.stold ?? "Stöld"}</SelectItem>
              <SelectItem value="brand">{dt.brand ?? "Brand"}</SelectItem>
              <SelectItem value="vilt">{dt.vilt ?? "Viltolycka"}</SelectItem>
              <SelectItem value="annat">{dt.annat ?? "Annat"}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>
            {l.damageDescription ?? "Detaljerad beskrivning av skadehändelsen"}
          </Label>
          <Textarea
            value={form.damageDescription}
            onChange={(e) =>
              setForm({ ...form, damageDescription: e.target.value })
            }
            placeholder={l.damageDescriptionPlaceholder}
            rows={3}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{l.damagedParts ?? "Skadade delar/områden"}</Label>
            <Input
              value={form.damagedParts}
              onChange={(e) =>
                setForm({ ...form, damagedParts: e.target.value })
              }
              placeholder={l.damagedPartsPlaceholder}
            />
          </div>
          <div className="space-y-2">
            <Label>{l.estimatedCost ?? "Uppskattat belopp (kr)"}</Label>
            <Input
              inputMode="numeric"
              value={form.estimatedCost}
              onChange={(e) =>
                setForm({
                  ...form,
                  estimatedCost: e.target.value.replace(/\D/g, ""),
                })
              }
            />
          </div>
        </div>
      </PdfFormSection>
      <PdfFormSection
        heading={l.sectionSketchCar ?? "Skadeskiss på fordonet"}
        description={
          l.sectionSketchCarDesc ??
          "Rita var på bilen skadan finns. Använd röd färg för att markera skadade områden."
        }
      >
        <DamageSketchCanvas
          type={form.carSketchType}
          onTypeChange={(type) => setForm({ ...form, carSketchType: type })}
          onSketchChange={(data) => setForm({ ...form, carSketchData: data })}
          width={300}
          height={300}
        />
      </PdfFormSection>
      <PdfFormSection heading={l.sectionSketchScene ?? "Olycksplatsskiss"}>
        <p className="text-sm text-muted-foreground mb-3">
          {l.sketchSceneIntro ??
            "Välj typ av vägmiljö och rita hur olyckan inträffade."}{" "}
          <strong className="text-foreground">
            {l.sketchLegendColors ?? "Eget fordon: rött. Motpart: blått."}
          </strong>
        </p>
        <DamageSketchCanvas
          type={form.sceneSketchType}
          onTypeChange={(type) => setForm({ ...form, sceneSketchType: type })}
          onSketchChange={(data) => setForm({ ...form, sceneSketchData: data })}
          width={300}
          height={300}
        />
      </PdfFormSection>
      {/* Övrig information: annan part, vittnen, polisanmälan */}
      <div className="space-y-3">
        <div className="rounded-lg border border-border bg-muted/30 p-2.5 space-y-2">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="insurance-hasCounterpart"
              checked={form.hasCounterpart}
              onCheckedChange={(c) => setForm({ ...form, hasCounterpart: !!c })}
            />
            <Label
              htmlFor="insurance-hasCounterpart"
              className="font-normal cursor-pointer"
            >
              {l.hasCounterpart ?? "Annan part inblandad"}
            </Label>
          </div>
          {form.hasCounterpart && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pl-6">
              <div className="space-y-2">
                <Label className="text-muted-foreground">
                  {l.counterpartName ?? "Motpartens namn"}
                </Label>
                <Input
                  value={form.counterpartName}
                  onChange={(e) =>
                    setForm({ ...form, counterpartName: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">
                  {l.counterpartPhone ?? "Telefon"}
                </Label>
                <Input
                  inputMode="tel"
                  value={form.counterpartPhone}
                  onChange={(e) =>
                    setForm({ ...form, counterpartPhone: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">
                  {l.counterpartInsurance ?? "Motpartens försäkringsbolag"}
                </Label>
                <Input
                  value={form.counterpartInsurance}
                  onChange={(e) =>
                    setForm({ ...form, counterpartInsurance: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">
                  {l.counterpartRegistration ?? "Motpartens reg.nr"}
                </Label>
                <Input
                  value={form.counterpartRegistration}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      counterpartRegistration: e.target.value,
                    })
                  }
                />
              </div>
            </div>
          )}
        </div>
        <div className="rounded-lg border border-border bg-muted/30 p-2.5 space-y-2">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="insurance-hasWitnesses"
              checked={form.hasWitnesses}
              onCheckedChange={(c) => setForm({ ...form, hasWitnesses: !!c })}
            />
            <Label
              htmlFor="insurance-hasWitnesses"
              className="font-normal cursor-pointer"
            >
              {l.hasWitnesses ?? "Vittnen finns"}
            </Label>
          </div>
          {form.hasWitnesses && (
            <div className="space-y-2 pl-6">
              <Label className="text-muted-foreground">
                {l.witnessInfoLabel ?? "Vittnenas namn och kontaktuppgifter"}
              </Label>
              <Textarea
                value={form.witnessInfo}
                onChange={(e) =>
                  setForm({ ...form, witnessInfo: e.target.value })
                }
                placeholder={l.witnessInfoPlaceholder}
                rows={3}
              />
            </div>
          )}
        </div>
        <div className="rounded-lg border border-border bg-muted/30 p-2.5 space-y-2">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="insurance-hasPoliceReport"
              checked={form.hasPoliceReport}
              onCheckedChange={(c) =>
                setForm({ ...form, hasPoliceReport: !!c })
              }
            />
            <Label
              htmlFor="insurance-hasPoliceReport"
              className="font-normal cursor-pointer"
            >
              {l.hasPoliceReport ?? "Polisanmälan gjord"}
            </Label>
          </div>
          {form.hasPoliceReport && (
            <div className="space-y-2 pl-6">
              <Label className="text-muted-foreground">
                {l.policeReportNumber ?? "Diarienummer"}
              </Label>
              <Input
                value={form.policeReportNumber}
                onChange={(e) =>
                  setForm({ ...form, policeReportNumber: e.target.value })
                }
                placeholder={l.policeReportPlaceholder}
              />
            </div>
          )}
        </div>
      </div>
      <PdfFormSection
        heading={l.sectionContact ?? "Kontaktuppgifter"}
        description={l.sectionContactDesc}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{l.contactPerson ?? l.contactName ?? "Kontaktperson"}</Label>
            <Input
              value={form.contactName}
              onChange={(e) =>
                setForm({ ...form, contactName: e.target.value })
              }
            />
          </div>
          <div className="space-y-2">
            <Label>
              {l.contactPhoneRequired ?? l.contactPhone ?? "Telefon"}
            </Label>
            <Input
              inputMode="tel"
              value={form.contactPhone}
              onChange={(e) =>
                setForm({
                  ...form,
                  contactPhone: e.target.value.replace(/\D/g, ""),
                })
              }
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label>{l.contactEmail ?? "E-post"}</Label>
          <Input
            type="email"
            value={form.contactEmail}
            onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
          />
        </div>
      </PdfFormSection>
      <PdfFormSection
        heading={l.sectionSignature ?? "Underskrift"}
        description={l.sectionSignatureDesc}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{l.signaturePlace ?? l.placePlaceholder ?? "Ort"}</Label>
            <Input
              value={form.signaturePlace}
              onChange={(e) =>
                setForm({ ...form, signaturePlace: e.target.value })
              }
              placeholder={l.placePlaceholder ?? "Ort"}
            />
          </div>
          <div className="space-y-2">
            <Label>{l.signatureDate ?? "Datum"}</Label>
            <div className="relative">
              <Input
                type="date"
                value={form.signatureDate}
                onChange={(e) =>
                  setForm({ ...form, signatureDate: e.target.value })
                }
              />
              <CalendarIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            </div>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mt-3">
          {l.certificationText ??
            "Jag intygar att uppgifterna ovan är korrekta och sanningsenliga."}
        </p>
      </PdfFormSection>
    </div>
  );
}

function ValuationFormFields({
  form,
  setForm,
  labels = {},
}: {
  form: ValuationFormData;
  setForm: React.Dispatch<React.SetStateAction<ValuationFormData | null>>;
  labels?: Record<string, string>;
}) {
  const l = labels;
  const { language } = useLanguage();
  const vcLocale = language === "en" ? enVehicleCard : svVehicleCard;
  const vc = vcLocale as {
    color: Record<string, string>;
    condition: Record<string, string>;
  };
  const translateFromVehicleCard = (
    category: "color" | "condition",
    value: string | undefined,
  ): string => {
    if (!value?.trim()) return "";
    const key = value.toLowerCase().replace(/\s+/g, "_");
    return vc[category][key] ?? value;
  };

  const addRemark = () => {
    setForm({
      ...form,
      remarks: [
        ...form.remarks,
        { id: crypto.randomUUID(), description: "", deduction: "" },
      ],
    });
  };
  const updateRemark = (
    id: string,
    field: "description" | "deduction",
    value: string,
  ) => {
    setForm({
      ...form,
      remarks: form.remarks.map((r) =>
        r.id === id ? { ...r, [field]: value } : r,
      ),
    });
  };
  const removeRemark = (id: string) => {
    setForm({
      ...form,
      remarks: form.remarks.filter((r) => r.id !== id),
    });
  };
  return (
    <div className="space-y-4">
      <PdfFormSection
        heading={l.sectionVehicle ?? "Fordons- och värderingsuppgifter"}
        description={l.sectionVehicleDesc}
      >
        <div className="space-y-2">
          <Label>{l.purposeLabel ?? l.purpose ?? "Syfte med värdering"}</Label>
          <Select
            value={form.purpose || "Forsaljning"}
            onValueChange={(val) => setForm({ ...form, purpose: val })}
          >
            <SelectTrigger className="h-8 text-xs bg-muted/50 border-border">
              <SelectValue
                placeholder={l.purposeOtherPlaceholder ?? "Välj syfte"}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Forsaljning">
                {l.purposeForsaljning ?? "Försäljning"}
              </SelectItem>
              <SelectItem value="Forsakring">
                {l.purposeForsakring ?? "Försäkring"}
              </SelectItem>
              <SelectItem value="Lan">
                {l.purposeLan ?? "Lån/Finansiering"}
              </SelectItem>
              <SelectItem value="Skattearende">
                {l.purposeSkattearende ?? "Skatteärende"}
              </SelectItem>
              <SelectItem value="Annat">{l.purposeAnnat ?? "Annat"}</SelectItem>
            </SelectContent>
          </Select>
          {form.purpose === "Annat" && (
            <div className="space-y-1.5 pt-1">
              <Label className="text-muted-foreground">
                {l.purposeOtherPlaceholder ?? "Ange syfte"}
              </Label>
              <Input
                value={form.purposeOther}
                onChange={(e) =>
                  setForm({ ...form, purposeOther: e.target.value })
                }
                placeholder={l.purposeOtherPlaceholder ?? "Ange syfte"}
                className="h-8 text-xs"
              />
            </div>
          )}
        </div>
        <div className="space-y-2">
          <Label>
            {l.estimatedPurchasePrice ?? "Uppskattat inköpspris (SEK)"}
          </Label>
          <Input
            value={form.estimatedPurchasePrice}
            onChange={(e) =>
              setForm({ ...form, estimatedPurchasePrice: e.target.value })
            }
          />
        </div>
      </PdfFormSection>
      <PdfFormSection
        heading={l.sectionRemarks ?? "Noteringar och avdrag"}
        description={l.sectionRemarksDesc}
      >
        <div className="space-y-2">
          <Label>{l.remarksDeductions ?? "Noteringar / Avdrag"}</Label>
          {form.remarks.map((r) => (
            <div key={r.id} className="flex gap-2 items-center">
              <Input
                placeholder={l.remarkDescription ?? "Beskrivning"}
                value={r.description}
                onChange={(e) =>
                  updateRemark(r.id, "description", e.target.value)
                }
                className="flex-1"
              />
              <Input
                placeholder={l.remarkDeduction ?? "Avdrag (kr)"}
                value={r.deduction}
                onChange={(e) =>
                  updateRemark(r.id, "deduction", e.target.value)
                }
                className="w-24"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeRemark(r.id)}
              >
                ×
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={addRemark}>
            + {l.addRemark ?? "Lägg till notering"}
          </Button>
        </div>
        <div className="space-y-2">
          <Label>{l.notes ?? "Övriga noteringar"}</Label>
          <Textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={2}
          />
        </div>
        <div className="space-y-2">
          <Label>{l.validityPeriod ?? "Giltighetstid (dagar)"}</Label>
          <Input
            value={form.validityPeriod}
            onChange={(e) =>
              setForm({ ...form, validityPeriod: e.target.value })
            }
          />
        </div>
      </PdfFormSection>
    </div>
  );
}

function ServiceSchemaFormFields({
  form,
  setForm,
  labels = {},
  types = {},
  intervals = {},
}: {
  form: ServiceSchemaFormData;
  setForm: React.Dispatch<React.SetStateAction<ServiceSchemaFormData | null>>;
  labels?: Record<string, string>;
  types?: Record<string, string>;
  intervals?: Record<string, string>;
}) {
  const l = labels;
  const { language } = useLanguage();

  return (
    <div className="space-y-4">
      <PdfFormSection
        heading={l.sectionCustomer ?? "Kunduppgifter"}
        description={l.sectionCustomerDesc}
      >
        <div className="space-y-2">
          <Label>{l.customerName ?? "Kundens namn"}</Label>
          <Input
            value={form.customerName}
            onChange={(e) => setForm({ ...form, customerName: e.target.value })}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{l.customerAddress ?? "Adress"}</Label>
            <Input
              value={form.customerAddress}
              onChange={(e) =>
                setForm({ ...form, customerAddress: e.target.value })
              }
            />
          </div>
          <div className="space-y-2">
            <Label>{l.customerPostal ?? "Postnr, Ort"}</Label>
            <Input
              value={form.customerPostal}
              onChange={(e) =>
                setForm({ ...form, customerPostal: e.target.value })
              }
            />
          </div>
        </div>
      </PdfFormSection>
      <PdfFormSection
        heading={l.sectionService ?? "Serviceuppgifter"}
        description={l.sectionServiceDesc}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{l.serviceType ?? "Typ av service"}</Label>
            <Select
              value={form.serviceType}
              onValueChange={(v) => setForm({ ...form, serviceType: v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Välj" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Basservice">
                  {types.basservice ?? "Basservice"}
                </SelectItem>
                <SelectItem value="Stor service">
                  {types.storService ?? "Stor service"}
                </SelectItem>
                <SelectItem value="Liten service">
                  {types.litenService ?? "Liten service"}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{l.serviceInterval ?? "Serviceintervall"}</Label>
            <Select
              value={form.serviceInterval}
              onValueChange={(v) => setForm({ ...form, serviceInterval: v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Välj" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1000mil/1år">
                  {intervals["1000mil1ar"] ?? "1000 mil/1 år"}
                </SelectItem>
                <SelectItem value="2000mil/1år">
                  {intervals["2000mil1ar"] ?? "2000 mil/1 år"}
                </SelectItem>
                <SelectItem value="3000mil/2år">
                  {intervals["3000mil2ar"] ?? "3000 mil/2 år"}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-2 mt-4">
          <Label>{l.extraService ?? "Extra service"}</Label>
          <Select
            value={form.extraService}
            onValueChange={(v) => setForm({ ...form, extraService: v })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Välj" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Ja">{l.extraServiceJa ?? "Ja"}</SelectItem>
              <SelectItem value="Nej">{l.extraServiceNej ?? "Nej"}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{l.date ?? "Datum"}</Label>
            <Input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label>{l.mileage ?? "Mätarställning (mil)"}</Label>
            <Input
              value={form.mileage}
              onChange={(e) => setForm({ ...form, mileage: e.target.value })}
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label>
            {l.serviceItems ?? "Servicepunkter (kryssa för utfört)"}
          </Label>
          <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-1">
            {serviceSchemaItemKeys.map((key) => {
              const label = getServiceSchemaItemLabels(language)[key] ?? key;
              return (
                <div key={key} className="flex items-center gap-2">
                  <Checkbox
                    checked={!!form.checkedItems[key]}
                    onCheckedChange={(c) =>
                      setForm({
                        ...form,
                        checkedItems: { ...form.checkedItems, [key]: !!c },
                      })
                    }
                  />
                  <span className="text-sm">{label}</span>
                  <Input
                    className="ml-auto w-32 h-7 text-xs"
                    placeholder={l.comment ?? "Kommentar"}
                    value={form.comments[key] ?? ""}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        comments: { ...form.comments, [key]: e.target.value },
                      })
                    }
                  />
                </div>
              );
            })}
          </div>
        </div>
        <div className="space-y-2">
          <Label>{l.signatureDate ?? "Datum (underskrift)"}</Label>
          <Input
            type="date"
            value={form.signatureDate}
            onChange={(e) =>
              setForm({ ...form, signatureDate: e.target.value })
            }
          />
        </div>
      </PdfFormSection>
    </div>
  );
}

function VDNFormFields({
  form,
  setForm,
  labels,
  vehicle,
}: {
  form: VDNFormData;
  setForm: React.Dispatch<React.SetStateAction<VDNFormData | null>>;
  labels?: Record<string, string>;
  vehicle: DealerPortalVehicle | null;
}) {
  const l = labels ?? {};
  const { language } = useLanguage();
  const vcLocale = language === "en" ? enVehicleCard : svVehicleCard;
  const vc = vcLocale as {
    transmission: Record<string, string>;
    fuelType: Record<string, string>;
    color: Record<string, string>;
    ui: { noEquipmentSpecified?: string };
  };

  const translateFromVehicleCard = (
    category: "transmission" | "fuelType" | "color",
    value: string | undefined,
  ): string => {
    if (!value?.trim()) return "";
    const key = value.toLowerCase().replace(/\s+/g, "_");
    return vc[category][key] ?? value;
  };

  return (
    <div className="space-y-4">
      <div className="p-3 bg-muted/50 rounded-lg text-sm text-muted-foreground">
        <strong className="text-foreground">
          {l.vdnIntroTitle ?? "Konsumentverkets Varudeklaration"}
        </strong>{" "}
        {l.vdnIntroBody ??
          "för begagnade personbilar. Alla uppgifter ska vara korrekta och fullständiga enligt Konsumentverkets riktlinjer."}
      </div>
      <PdfFormSection
        heading={l.sectionSeller ?? "Säljare"}
        description={l.sectionSellerDesc}
      >
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs">
              {l.sellerName ?? "Säljare – Namn"}
            </Label>
            <Input
              value={form.sellerName}
              onChange={(e) => setForm({ ...form, sellerName: e.target.value })}
              className="h-9"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{l.sellerOrgNr ?? "Org.nr"}</Label>
            <Input
              value={form.sellerOrgNr}
              onChange={(e) =>
                setForm({ ...form, sellerOrgNr: e.target.value })
              }
              className="h-9"
            />
          </div>
          <div className="space-y-1.5 col-span-2">
            <Label className="text-xs">{l.sellerAddress ?? "Adress"}</Label>
            <Input
              value={form.sellerAddress}
              onChange={(e) =>
                setForm({ ...form, sellerAddress: e.target.value })
              }
              className="h-9"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">
              {l.sellerPostalCity ?? "Postnr, Ort"}
            </Label>
            <Input
              value={form.sellerPostalCity}
              onChange={(e) =>
                setForm({ ...form, sellerPostalCity: e.target.value })
              }
              className="h-9"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{l.sellerPhone ?? "Telefon"}</Label>
            <Input
              value={form.sellerPhone}
              onChange={(e) =>
                setForm({ ...form, sellerPhone: e.target.value })
              }
              className="h-9"
            />
          </div>
          <div className="space-y-1.5 col-span-2">
            <Label className="text-xs">{l.sellerEmail ?? "E-post"}</Label>
            <Input
              type="email"
              value={form.sellerEmail}
              onChange={(e) =>
                setForm({ ...form, sellerEmail: e.target.value })
              }
              className="h-9"
            />
          </div>
        </div>
      </PdfFormSection>
      <PdfFormSection
        heading={l.sectionBuyer ?? "Köpare och fordon"}
        description={l.sectionBuyerDesc}
      >
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs">{l.buyerName ?? "Köpare – Namn"}</Label>
            <Input
              value={form.buyerName}
              onChange={(e) => setForm({ ...form, buyerName: e.target.value })}
              className="h-9"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{l.buyerPersonNr ?? "Personnr"}</Label>
            <Input
              value={form.buyerPersonNr}
              onChange={(e) =>
                setForm({ ...form, buyerPersonNr: e.target.value })
              }
              placeholder={l.buyerPersonNrPlaceholder ?? "ÅÅÅÅMMDD-XXXX"}
              className="h-9"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{l.buyerAddress ?? "Adress"}</Label>
            <Input
              value={form.buyerAddress}
              onChange={(e) =>
                setForm({ ...form, buyerAddress: e.target.value })
              }
              className="h-9"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">
              {l.buyerPostalCity ?? "Postnr, Ort"}
            </Label>
            <Input
              value={form.buyerPostalCity}
              onChange={(e) =>
                setForm({ ...form, buyerPostalCity: e.target.value })
              }
              className="h-9"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{l.buyerPhone ?? "Telefon"}</Label>
            <Input
              value={form.buyerPhone}
              onChange={(e) => setForm({ ...form, buyerPhone: e.target.value })}
              className="h-9"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{l.buyerEmail ?? "E-post"}</Label>
            <Input
              type="email"
              value={form.buyerEmail}
              onChange={(e) => setForm({ ...form, buyerEmail: e.target.value })}
              className="h-9"
            />
          </div>
        </div>
      </PdfFormSection>
      <PdfFormSection
        heading={l.sectionVehicle ?? "Fordonsuppgifter"}
        description={l.sectionVehicleDesc}
      >
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <div className="space-y-1.5">
            <Label className="text-xs">{l.regNr ?? "Reg.nr"}</Label>
            <Input
              value={form.registration}
              onChange={(e) =>
                setForm({ ...form, registration: e.target.value })
              }
              className="h-9 font-mono uppercase"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{l.make ?? "Fabrikat"}</Label>
            <Input
              value={form.make}
              onChange={(e) => setForm({ ...form, make: e.target.value })}
              className="h-9"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{l.model ?? "Modell"}</Label>
            <Input
              value={form.model}
              onChange={(e) => setForm({ ...form, model: e.target.value })}
              className="h-9"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{l.variant ?? "Variant"}</Label>
            <Input
              value={form.variant}
              onChange={(e) => setForm({ ...form, variant: e.target.value })}
              placeholder="Ex: Sport, Titanium"
              className="h-9"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{l.modelYear ?? "Årsmodell"}</Label>
            <Input
              value={form.modelYear}
              onChange={(e) => setForm({ ...form, modelYear: e.target.value })}
              className="h-9"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">
              {l.firstRegDate ?? "1:a reg.datum"}
            </Label>
            <Input
              type="date"
              value={form.firstRegistrationDate}
              onChange={(e) =>
                setForm({ ...form, firstRegistrationDate: e.target.value })
              }
              className="h-9"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">
              {l.numberOfOwners ?? "Antal ägare"}
            </Label>
            <Input
              value={form.numberOfOwners}
              onChange={(e) =>
                setForm({ ...form, numberOfOwners: e.target.value })
              }
              className="h-9"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">
              {l.numberOfKeys ?? "Antal nycklar"}
            </Label>
            <Input
              value={form.numberOfKeys}
              onChange={(e) =>
                setForm({ ...form, numberOfKeys: e.target.value })
              }
              className="h-9"
            />
          </div>
        </div>
        <div className="space-y-1.5 mt-3">
          <Label className="text-xs">{l.vin ?? "Chassinummer (VIN)"}</Label>
          <Input
            value={form.vin}
            onChange={(e) => setForm({ ...form, vin: e.target.value })}
            className="h-9 font-mono"
          />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
          <div className="space-y-1.5 flex flex-col col-span-2 sm:col-span-1">
            <Label className="text-xs">{l.mileage ?? "Mätarställning"}</Label>
            <Input
              inputMode="numeric"
              value={form.mileage}
              onChange={(e) =>
                setForm({ ...form, mileage: e.target.value.replace(/\D/g, "") })
              }
              className="h-9 w-full min-w-[6rem] max-w-[10rem]"
            />
            <label className="flex items-center gap-1.5 cursor-pointer mt-1">
              <Checkbox
                id="vdn-mileageGuaranteed"
                checked={form.mileageGuaranteed}
                onCheckedChange={(c) =>
                  setForm({ ...form, mileageGuaranteed: !!c })
                }
              />
              <span className="text-xs whitespace-nowrap">
                {l.mileageGuaranteed ?? "Garanterad"}
              </span>
            </label>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{l.color ?? "Färg"}</Label>
            <Input
              value={form.color}
              onChange={(e) => setForm({ ...form, color: e.target.value })}
              className="h-9"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{l.fuel ?? "Drivmedel"}</Label>
            <Input
              value={form.fuel}
              onChange={(e) => setForm({ ...form, fuel: e.target.value })}
              className="h-9"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{l.transmission ?? "Växellåda"}</Label>
            <Input
              value={form.transmission}
              onChange={(e) =>
                setForm({ ...form, transmission: e.target.value })
              }
              className="h-9"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{l.engineVolume ?? "Motorvolym"}</Label>
            <Input
              value={form.engineVolume}
              onChange={(e) =>
                setForm({ ...form, engineVolume: e.target.value })
              }
              placeholder="cc/l"
              className="h-9"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{l.enginePower ?? "Effekt (hk)"}</Label>
            <Input
              inputMode="numeric"
              value={form.enginePower}
              onChange={(e) =>
                setForm({
                  ...form,
                  enginePower: e.target.value.replace(/\D/g, ""),
                })
              }
              className="h-9"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">
              {l.environmentClass ?? "Miljöklass"}
            </Label>
            <Input
              value={form.environmentClass}
              onChange={(e) =>
                setForm({ ...form, environmentClass: e.target.value })
              }
              className="h-9"
            />
          </div>
        </div>
        <div className="space-y-1.5 mt-3 max-w-xs">
          <Label className="text-xs">{l.co2Emission ?? "CO2 (g/km)"}</Label>
          <Input
            value={form.co2Emission}
            onChange={(e) => setForm({ ...form, co2Emission: e.target.value })}
            className="h-9"
          />
        </div>
        <div className="flex items-center gap-2 mt-3">
          <Checkbox
            id="vdn-regCert"
            checked={form.registrationCertificate}
            onCheckedChange={(c) =>
              setForm({ ...form, registrationCertificate: !!c })
            }
          />
          <Label htmlFor="vdn-regCert" className="text-sm">
            {l.regCertIncluded ?? "Registreringsbevis medföljer"}
          </Label>
        </div>
      </PdfFormSection>
      <PdfFormSection
        heading={l.sectionImportTax ?? "Import och skatt"}
        description=""
      >
        <div className="flex items-center gap-2">
          <Checkbox
            id="vdn-import"
            checked={form.importVehicle}
            onCheckedChange={(c) => setForm({ ...form, importVehicle: !!c })}
          />
          <Label htmlFor="vdn-import">
            {l.importVehicle ?? "Importfordon"}
          </Label>
        </div>
        {form.importVehicle && (
          <div className="space-y-2 mt-2">
            <Label>{l.importCountry ?? "Importland"}</Label>
            <Input
              value={form.importCountry}
              onChange={(e) =>
                setForm({ ...form, importCountry: e.target.value })
              }
            />
          </div>
        )}
        <div className="flex items-center gap-2 mt-2">
          <Checkbox
            id="vdn-taxPaid"
            checked={form.taxPaid}
            onCheckedChange={(c) => setForm({ ...form, taxPaid: !!c })}
          />
          <Label htmlFor="vdn-taxPaid">
            {l.taxPaid ?? "Fordonsskatt betald"}
          </Label>
        </div>
        {!form.taxPaid && (
          <div className="space-y-2 mt-2">
            <Label>{l.taxDebtAmount ?? "Skuldbelopp (kr)"}</Label>
            <Input
              value={form.taxDebtAmount}
              onChange={(e) =>
                setForm({ ...form, taxDebtAmount: e.target.value })
              }
            />
          </div>
        )}
      </PdfFormSection>
      <PdfFormSection
        heading={l.sectionInspection ?? "Besiktning"}
        description=""
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div className="space-y-1.5">
            <Label className="text-xs">
              {l.lastInspection ?? "Senaste besiktning"}
            </Label>
            <Input
              type="date"
              value={form.lastInspectionDate}
              onChange={(e) =>
                setForm({ ...form, lastInspectionDate: e.target.value })
              }
              className="h-9"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">
              {l.inspectionResult ?? "Resultat"}
            </Label>
            <Select
              value={form.inspectionResult}
              onValueChange={(v) => setForm({ ...form, inspectionResult: v })}
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder={l.selectPlaceholder ?? "Välj"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="approved">
                  {l.inspectionResultApproved ?? "Godkänd"}
                </SelectItem>
                <SelectItem value="approved_with_remarks">
                  {l.inspectionResultApprovedWithRemarks ??
                    "Godkänd med anmärkning"}
                </SelectItem>
                <SelectItem value="not_approved">
                  {l.inspectionResultNotApproved ?? "Ej godkänd"}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">
              {l.nextInspection ?? "Nästa besiktning"}
            </Label>
            <Input
              type="date"
              value={form.nextInspectionDate}
              onChange={(e) =>
                setForm({ ...form, nextInspectionDate: e.target.value })
              }
              className="h-9"
            />
          </div>
        </div>
        {(form.inspectionResult === "approved_with_remarks" ||
          form.inspectionRemarks) && (
          <div className="space-y-1.5 mt-2">
            <Label className="text-xs">
              {l.inspectionRemarksLabel ?? "Besiktningsanmärkningar"}
            </Label>
            <Input
              value={form.inspectionRemarks}
              onChange={(e) =>
                setForm({ ...form, inspectionRemarks: e.target.value })
              }
              placeholder={
                l.inspectionRemarksPlaceholder ??
                "Ange besiktningsanmärkningar..."
              }
              className="h-9"
            />
          </div>
        )}
      </PdfFormSection>
      <PdfFormSection
        heading={l.sectionService ?? "Service och underhåll"}
        description=""
      >
        <div className="flex flex-wrap items-center gap-4 gap-y-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              id="vdn-serviceBook"
              checked={form.serviceBookExists}
              onCheckedChange={(c) =>
                setForm({ ...form, serviceBookExists: !!c })
              }
            />
            <span className="text-sm">
              {l.serviceBook ?? "Servicebok finns"}
            </span>
          </label>
          {form.serviceBookExists && (
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                id="vdn-serviceBookDigital"
                checked={form.serviceBookDigital}
                onCheckedChange={(c) =>
                  setForm({ ...form, serviceBookDigital: !!c })
                }
              />
              <span className="text-sm">
                {l.serviceBookDigital ?? "Digital"}
              </span>
            </label>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
          <div className="space-y-1.5">
            <Label className="text-xs">
              {l.lastService ?? "Senaste service"}
            </Label>
            <Input
              type="date"
              value={form.lastServiceDate}
              onChange={(e) =>
                setForm({ ...form, lastServiceDate: e.target.value })
              }
              className="h-9"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">
              {l.lastServiceMileage ?? "Vid mätarst."}
            </Label>
            <Input
              value={form.lastServiceMileage}
              onChange={(e) =>
                setForm({ ...form, lastServiceMileage: e.target.value })
              }
              className="h-9"
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4 gap-y-2 mt-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              id="vdn-timingBelt"
              checked={form.timingBeltChanged}
              onCheckedChange={(c) =>
                setForm({ ...form, timingBeltChanged: !!c })
              }
            />
            <span className="text-sm">
              {l.timingBeltChanged ?? "Kamrem bytt"}
            </span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              id="vdn-testDrive"
              checked={form.testDrivePerformed}
              onCheckedChange={(c) =>
                setForm({ ...form, testDrivePerformed: !!c })
              }
            />
            <span className="text-sm">
              {l.testDrivePerformed ?? "Provkörning utförd"}
            </span>
          </label>
        </div>
        {form.timingBeltChanged && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
            <div className="space-y-1.5">
              <Label className="text-xs">{l.date ?? "Datum"}</Label>
              <Input
                type="date"
                value={form.timingBeltDate}
                onChange={(e) =>
                  setForm({ ...form, timingBeltDate: e.target.value })
                }
                className="h-9"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">
                {l.lastServiceMileage ?? "Vid mil"}
              </Label>
              <Input
                value={form.timingBeltMileage}
                onChange={(e) =>
                  setForm({ ...form, timingBeltMileage: e.target.value })
                }
                className="h-9"
              />
            </div>
          </div>
        )}
        {form.testDrivePerformed && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
            <div className="space-y-1.5">
              <Label className="text-xs">{l.date ?? "Datum"}</Label>
              <Input
                type="date"
                value={form.testDriveDate}
                onChange={(e) =>
                  setForm({ ...form, testDriveDate: e.target.value })
                }
                className="h-9"
              />
            </div>
          </div>
        )}
      </PdfFormSection>
      <PdfFormSection
        heading={l.sectionDamageRepairs ?? "Tidigare skador och reparationer"}
        description=""
      >
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-2">
            <Checkbox
              checked={form.hasAccidentHistory}
              onCheckedChange={(c) =>
                setForm({ ...form, hasAccidentHistory: !!c })
              }
            />
            <span className="text-sm">
              {l.accidentVehicle ?? "Olycksfordon"}
            </span>
          </label>
          <label className="flex items-center gap-2">
            <Checkbox
              checked={form.hasRepaintedParts}
              onCheckedChange={(c) =>
                setForm({ ...form, hasRepaintedParts: !!c })
              }
            />
            <span className="text-sm">
              {l.repaintedParts ?? "Omlackerade delar"}
            </span>
          </label>
          <label className="flex items-center gap-2">
            <Checkbox
              checked={form.hasRustDamage}
              onCheckedChange={(c) => setForm({ ...form, hasRustDamage: !!c })}
            />
            <span className="text-sm">{l.rustDamage ?? "Rostskador"}</span>
          </label>
        </div>
        {form.hasAccidentHistory && (
          <div className="space-y-2 mt-2">
            <Label>{l.accidentDescription ?? "Skadebeskrivning"}</Label>
            <Textarea
              value={form.accidentDescription}
              onChange={(e) =>
                setForm({ ...form, accidentDescription: e.target.value })
              }
              rows={2}
            />
          </div>
        )}
        {form.hasRepaintedParts && (
          <div className="space-y-2 mt-2">
            <Label>{l.repaintedDescription ?? "Omlackering beskrivning"}</Label>
            <Input
              value={form.repaintedPartsDescription}
              onChange={(e) =>
                setForm({ ...form, repaintedPartsDescription: e.target.value })
              }
            />
          </div>
        )}
        {form.hasRustDamage && (
          <div className="space-y-2 mt-2">
            <Label>{l.rustDescription ?? "Rost beskrivning"}</Label>
            <Input
              value={form.rustDescription}
              onChange={(e) =>
                setForm({ ...form, rustDescription: e.target.value })
              }
            />
          </div>
        )}
      </PdfFormSection>
      <PdfFormSection
        heading={l.sectionTechnicalCondition ?? "Tekniskt skick (bedömning)"}
        description=""
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            ["engineCondition", "conditionEngine"],
            ["transmissionCondition", "conditionTransmission"],
            ["brakeCondition", "conditionBrakes"],
            ["suspensionCondition", "conditionSuspension"],
            ["bodyCondition", "conditionBody"],
            ["interiorCondition", "conditionInterior"],
            ["electricalCondition", "conditionElectrical"],
            ["exhaustCondition", "conditionExhaust"],
            ["steeringCondition", "conditionSteering"],
            ["lightsCondition", "conditionLights"],
          ].map(([key, labelKey]) => (
            <div key={key} className="space-y-2">
              <Label>{(l as Record<string, string>)[labelKey] ?? "—"}</Label>
              <Select
                value={form[key as keyof VDNFormData] as string}
                onValueChange={(v) => setForm({ ...form, [key]: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={l.selectPlaceholder ?? "Välj"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Utmärkt">
                    {l.conditionExcellent ?? "Utmärkt"}
                  </SelectItem>
                  <SelectItem value="Bra">
                    {l.conditionGood ?? "Bra"}
                  </SelectItem>
                  <SelectItem value="Normalt">
                    {l.conditionNormal ?? "Normalt"}
                  </SelectItem>
                  <SelectItem value="Slitet">
                    {l.conditionWorn ?? "Slitet"}
                  </SelectItem>
                  <SelectItem value="Dåligt">
                    {l.conditionPoor ?? "Dåligt"}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      </PdfFormSection>
      <PdfFormSection
        heading={l.sectionTyresWheels ?? "Däck och hjul"}
        description=""
      >
        <div className="space-y-3">
          <div>
            <label className="flex items-center gap-2">
              <Checkbox
                checked={form.summerTiresIncluded}
                onCheckedChange={(c) =>
                  setForm({ ...form, summerTiresIncluded: !!c })
                }
              />
              <span className="text-sm">{l.summerTyres ?? "Sommardäck"}</span>
            </label>
            {form.summerTiresIncluded && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2 ml-6">
                <Input
                  placeholder={l.tireBrand ?? "Märke"}
                  value={form.summerTiresBrand}
                  onChange={(e) =>
                    setForm({ ...form, summerTiresBrand: e.target.value })
                  }
                />
                <Input
                  placeholder={l.tireDimension ?? "Dimension"}
                  value={form.summerTiresDimension}
                  onChange={(e) =>
                    setForm({ ...form, summerTiresDimension: e.target.value })
                  }
                />
                <Input
                  placeholder={l.treadDepthMm ?? "Mönsterdjup mm"}
                  value={form.summerTiresDepth}
                  onChange={(e) =>
                    setForm({ ...form, summerTiresDepth: e.target.value })
                  }
                />
              </div>
            )}
          </div>
          <div>
            <label className="flex items-center gap-2">
              <Checkbox
                checked={form.winterTiresIncluded}
                onCheckedChange={(c) =>
                  setForm({ ...form, winterTiresIncluded: !!c })
                }
              />
              <span className="text-sm">{l.winterTyres ?? "Vinterdäck"}</span>
            </label>
            {form.winterTiresIncluded && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2 ml-6">
                <Input
                  placeholder={l.tireBrand ?? "Märke"}
                  value={form.winterTiresBrand}
                  onChange={(e) =>
                    setForm({ ...form, winterTiresBrand: e.target.value })
                  }
                />
                <Input
                  placeholder={l.tireDimension ?? "Dimension"}
                  value={form.winterTiresDimension}
                  onChange={(e) =>
                    setForm({ ...form, winterTiresDimension: e.target.value })
                  }
                />
                <Input
                  placeholder={l.treadDepthMm ?? "Mönsterdjup mm"}
                  value={form.winterTiresDepth}
                  onChange={(e) =>
                    setForm({ ...form, winterTiresDepth: e.target.value })
                  }
                />
              </div>
            )}
          </div>
          <label className="flex items-center gap-2">
            <Checkbox
              checked={form.spareTireIncluded}
              onCheckedChange={(c) =>
                setForm({ ...form, spareTireIncluded: !!c })
              }
            />
            <span className="text-sm">{l.spareWheel ?? "Reservhjul"}</span>
          </label>
        </div>
      </PdfFormSection>
      <div className="rounded-lg border border-green-200/60 bg-green-50/80 dark:bg-green-950/30 dark:border-green-800/50 p-2.5 shadow-sm">
        <h3 className="text-xs font-semibold text-muted-foreground mb-2">
          {l.evSection ?? "Elbil / Hybrid"}
        </h3>
        <div className="flex items-center gap-2 mb-3">
          <Checkbox
            id="vdn-electric"
            checked={form.isElectricOrHybrid}
            onCheckedChange={(c) =>
              setForm({ ...form, isElectricOrHybrid: !!c })
            }
          />
          <Label
            htmlFor="vdn-electric"
            className="text-sm font-normal cursor-pointer"
          >
            {l.evOrHybrid ?? "Elbil eller laddhybrid"}
          </Label>
        </div>
        {form.isElectricOrHybrid && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            <div className="space-y-1.5">
              <Label className="text-xs">
                {l.batteryCapacityKwh ?? "Batterikapacitet (kWh)"}
              </Label>
              <Input
                value={form.batteryCapacity}
                onChange={(e) =>
                  setForm({ ...form, batteryCapacity: e.target.value })
                }
                placeholder="kWh"
                className="h-9 bg-background"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">
                {l.batteryHealthPct ?? "Batterihälsa (%)"}
              </Label>
              <Input
                value={form.batteryHealth}
                onChange={(e) =>
                  setForm({ ...form, batteryHealth: e.target.value })
                }
                placeholder="%"
                className="h-9 bg-background"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{l.rangeKm ?? "Räckvidd (km)"}</Label>
              <Input
                value={form.estimatedRange}
                onChange={(e) =>
                  setForm({ ...form, estimatedRange: e.target.value })
                }
                placeholder="km"
                className="h-9 bg-background"
              />
            </div>
            <div className="space-y-1.5 flex flex-col justify-end">
              <label className="flex items-center gap-2 cursor-pointer min-h-9">
                <Checkbox
                  checked={form.chargingCableIncluded}
                  onCheckedChange={(c) =>
                    setForm({ ...form, chargingCableIncluded: !!c })
                  }
                />
                <span className="text-sm">
                  {l.chargingCable ?? "Laddkabel"}
                </span>
              </label>
            </div>
          </div>
        )}
      </div>
      <PdfFormSection
        heading={l.equipmentSection ?? "Utrustning"}
        description={
          l.equipmentFromCard ??
          "Utrustning från fordonskortet (Fordonsdetaljer). Gå dit för att ändra."
        }
      >
        {vehicle?.equipment?.length ? (
          <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm">
            <p className="text-muted-foreground text-xs mb-1">
              {l.equipmentFromCardList ?? "Från fordonskortet:"}{" "}
              {vehicle.equipment.join(", ")}
            </p>
          </div>
        ) : (
          <p className="text-muted-foreground text-xs">
            {l.equipmentEmpty ??
              "Ingen utrustning angiven i Fordonsdetaljer. Gå dit för att lägga till."}
          </p>
        )}
      </PdfFormSection>
      <PdfFormSection
        heading={l.sectionRemarksDefects ?? "Anmärkningar och kända brister"}
        description=""
      >
        <div className="space-y-2">
          <Label>{l.knownDefectsLabel ?? "Kända brister"}</Label>
          <Textarea
            value={form.knownDefects}
            onChange={(e) => setForm({ ...form, knownDefects: e.target.value })}
            rows={2}
          />
        </div>
        <div className="space-y-2 mt-2">
          <Label>{l.otherRemarksLabel ?? "Övriga anmärkningar"}</Label>
          <Textarea
            value={form.remarks}
            onChange={(e) => setForm({ ...form, remarks: e.target.value })}
            rows={2}
          />
        </div>
      </PdfFormSection>
      <div className="rounded-lg border border-border bg-muted/30 dark:bg-muted/20 p-2.5 space-y-2">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          {l.guaranteeSection ?? "Garanti"}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div className="space-y-1">
            <Label className="text-sm font-normal">
              {l.guaranteeTypeLabel ?? "Garantityp"}
            </Label>
            <Select
              value={form.guaranteeType}
              onValueChange={(v) => setForm({ ...form, guaranteeType: v })}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue placeholder={l.selectPlaceholder ?? "Välj"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="konsument6">
                  {l.guaranteeConsumer6 ?? "Konsumentgaranti 6 mån"}
                </SelectItem>
                <SelectItem value="konsument12">
                  {l.guaranteeConsumer12 ?? "Konsumentgaranti 12 mån"}
                </SelectItem>
                <SelectItem value="trafiksakerhet">
                  {l.guaranteeTrafficSafety ?? "Trafiksäkerhetsgaranti"}
                </SelectItem>
                <SelectItem value="ingen">
                  {l.guaranteeNone ?? "Ingen garanti"}
                </SelectItem>
                <SelectItem value="annan">
                  {l.guaranteeOther ?? "Annan garanti"}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-sm font-normal">
              {l.validityPeriodLabel ?? "Giltighetstid"}
            </Label>
            <Input
              value={form.guaranteePeriod}
              onChange={(e) =>
                setForm({ ...form, guaranteePeriod: e.target.value })
              }
              placeholder={l.validityPeriodPlaceholder ?? "t.ex. 12 mån"}
              className="h-8 text-sm"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-sm font-normal">
              {l.maxMileageMil ?? "Maxkörsträcka (mil)"}
            </Label>
            <Input
              value={form.guaranteeMileage}
              onChange={(e) =>
                setForm({ ...form, guaranteeMileage: e.target.value })
              }
              className="h-8 text-sm"
            />
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-sm font-normal">
            {l.guaranteeConditionsLabel ?? "Garantivillkor och begränsningar"}
          </Label>
          <Textarea
            value={form.guaranteeConditions}
            onChange={(e) =>
              setForm({ ...form, guaranteeConditions: e.target.value })
            }
            rows={2}
            className="text-sm min-h-[4rem]"
          />
        </div>
      </div>
      <div className="rounded-lg border border-amber-200/60 bg-amber-50/80 dark:bg-amber-900/10 dark:border-amber-800/50 p-2.5">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
          {l.reclamationTitle ?? "Reklamationsrätt enligt konsumentköplagen"}
        </h3>
        <Textarea
          value={form.reclamationInfo}
          onChange={(e) =>
            setForm({ ...form, reclamationInfo: e.target.value })
          }
          rows={3}
          placeholder={
            l.reclamationPlaceholder ??
            "Enligt konsumentköplagen har köparen rätt att reklamera fel på bilen i tre år från köpet."
          }
          className="bg-transparent border-0 focus-visible:ring-0 resize-none text-sm placeholder:text-muted-foreground"
        />
      </div>
      <div className="rounded-lg border border-border bg-muted/30 dark:bg-muted/20 p-2.5 space-y-2">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          {l.sectionPricePayment ?? "Pris och betalning"}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          <div className="space-y-1">
            <Label className="text-sm font-normal">
              {l.price ?? "Pris (SEK)"} *
            </Label>
            <Input
              inputMode="numeric"
              value={form.price}
              onChange={(e) =>
                setForm({ ...form, price: e.target.value.replace(/\D/g, "") })
              }
              className="h-8 text-sm"
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer self-end pb-2">
            <Checkbox
              id="vdn-vat"
              checked={form.vatIncluded}
              onCheckedChange={(c) => setForm({ ...form, vatIncluded: !!c })}
            />
            <span className="text-xs">{l.vatIncluded ?? "Inkl. moms"}</span>
          </label>
          <div className="space-y-1">
            <Label className="text-sm font-normal">
              {l.depositKr ?? "Handpenning (kr)"}
            </Label>
            <Input
              value={form.depositAmount}
              onChange={(e) =>
                setForm({ ...form, depositAmount: e.target.value })
              }
              className="h-8 text-sm"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-sm font-normal">
              {l.paymentMethodLabel ?? "Betalningssätt"}
            </Label>
            <Select
              value={form.paymentMethod}
              onValueChange={(v) => setForm({ ...form, paymentMethod: v })}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue placeholder={l.selectPlaceholder ?? "Välj"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="kontant">
                  {l.paymentKontant ?? "Kontant"}
                </SelectItem>
                <SelectItem value="bank">
                  {l.paymentBank ?? "Banköverföring"}
                </SelectItem>
                <SelectItem value="finansiering">
                  {l.paymentFinancing ?? "Finansiering"}
                </SelectItem>
                <SelectItem value="leasing">
                  {l.paymentLeasing ?? "Leasing"}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox
            id="vdn-financing"
            checked={form.financingArranged}
            onCheckedChange={(c) =>
              setForm({ ...form, financingArranged: !!c })
            }
          />
          <span className="text-xs">
            {l.financingArranged ?? "Finansiering ordnad"}
          </span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1 border-t border-border/50">
          <div className="space-y-1">
            <Label className="text-sm font-normal">
              {l.deliveryDateLabel ?? "Leveransdatum"}
            </Label>
            <Input
              type="date"
              value={form.deliveryDate}
              onChange={(e) =>
                setForm({ ...form, deliveryDate: e.target.value })
              }
              className="h-8 text-sm"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-sm font-normal">
              {l.deliveryLocationLabel ?? "Leveransplats"}
            </Label>
            <Input
              value={form.deliveryLocation}
              onChange={(e) =>
                setForm({ ...form, deliveryLocation: e.target.value })
              }
              className="h-8 text-sm"
            />
          </div>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox
            id="vdn-distanceSale"
            checked={form.isDistanceSale}
            onCheckedChange={(c) => setForm({ ...form, isDistanceSale: !!c })}
          />
          <span className="text-xs">
            {l.distanceSale ?? "Distansköp (ångerrätt)"}
          </span>
        </label>
        {form.isDistanceSale && (
          <div className="space-y-1">
            <Label className="text-xs font-normal">
              {l.withdrawalRightInfoLabel ?? "Information om ångerrätt"}
            </Label>
            <Textarea
              value={form.withdrawalRightInfo}
              onChange={(e) =>
                setForm({ ...form, withdrawalRightInfo: e.target.value })
              }
              rows={2}
              className="text-sm min-h-[3rem]"
            />
          </div>
        )}
      </div>
      <div className="rounded-lg border border-border bg-muted/30 dark:bg-muted/20 p-3 space-y-3">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          {l.sectionSignaturesLabel ?? "Underskrifter"}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-xs font-normal">Datum</Label>
            <Input
              type="date"
              value={form.signatureDate}
              onChange={(e) =>
                setForm({ ...form, signatureDate: e.target.value })
              }
              className="h-8 text-sm"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-normal">Ort</Label>
            <Input
              value={form.signaturePlace}
              onChange={(e) =>
                setForm({ ...form, signaturePlace: e.target.value })
              }
              placeholder={l.placePlaceholder ?? "Ort"}
              className="h-8 text-sm"
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">
          {l.signatoriesText ??
            "Undertecknade parter godkänner ovanstående uppgifter. Köparen bekräftar att ha tagit del av all information i varudeklarationen före köpet. Varudeklarationen är framtagen enligt Konsumentverkets riktlinjer."}
        </p>
      </div>
    </div>
  );
}
